import { isEmpty, isString, isObject, isNil, get } from "lodash";
import * as funcParser from "./func";

const PROPS_IN_DETAIL = ["visible", "hiddenAll"];
// 需要递归赋值的属性，目前只有这些属性可能出现:对象子属性中存在表达式
const PROPS_NEED_ITERATION = ["overwriteProps", "internalProps"];

// 在详情页rule或request异步更新target时，增加允许指定组件中的字段回写，而不仅仅是PROPS_IN_DETAIL中配置的全量组件维度的指定字段
const COMP_PROPS_UPDATE_IN_DETAIL = [
  {
    componentName: "SearchInput",
    props: ["dataSource.options", "options"],
  },
];

/**
 * 在详情页情况下，判断异步是否允许的组件及相应的字段更新回写。
 * 例如SearchInput异步下拉数据，用于异步下拉数据匹配field value来展示用户能够感知的text文本
 * @param {*} componentName
 * @param {*} propName
 * @returns
 */
function _isCompPropsAllowedInDetail(componentName, propName) {
  if (componentName) {
    const item = COMP_PROPS_UPDATE_IN_DETAIL.find(
      (i) => i.componentName === componentName
    );
    if (
      item &&
      Array.isArray(item.props) &&
      item.props.indexOf(propName) > -1
    ) {
      return true;
    }
  }

  return false;
}

/**
 * 校验
 * @param {object} target 目标
 */
export const validate = (target) => {
  if (!isObject(target)) {
    return {
      success: false,
      errorMsg: `${target} 影响目标须为对象`,
    };
  }

  return {
    success: true,
    errorMsg: "",
  };
};

export const filterDetailProps = (opts = {}, props, result = {}) => {
  Object.keys(props).forEach((key) => {
    const value = props[key];
    if (_isCompPropsAllowedInDetail(get(opts || {}, "componentName"), key)) {
      result[key] = value;
    } else if (Array.isArray(value)) {
      const tempRes = filterDetailProps(opts, value, []);
      if (!isEmpty(tempRes)) {
        result[key] = tempRes;
      }
    } else if (isObject(value)) {
      const tempRes = filterDetailProps(opts, value);
      if (!isEmpty(tempRes)) {
        result[key] = tempRes;
      }
    } else if (PROPS_IN_DETAIL.indexOf(key) > -1) {
      result[key] = value;
    }
  });
  return result;
};

/**
 * 解析目标对象
 * @param {string} target 目标对象
 * @return {object} { event, handler }
 */
export const parse = (target = {}, options = {}) => {
  const { fieldApi, isDetail, isEdit, overwrite = true } = options;

  const validateResult = validate(target);
  console.warn(validateResult.success, validateResult.errorMsg);

  // 目前只支持值变更
  const event = {};
  const fieldsShouldReset = []; // 值需要重置的字段
  Object.keys(target).forEach((fieldId) => {
    const { props: inProps } = target[fieldId];
    Object.keys(inProps || {}).forEach((propName) => {
      // 选项更新，字段的值需要重置
      if (
        propName.indexOf("options") !== -1 ||
        propName.indexOf("Options") !== -1
      ) {
        fieldsShouldReset.push(fieldId);
      }
      if (isString(inProps[propName])) {
        (inProps[propName].match(/(\${([^}]*)})/g) || []).forEach((item) => {
          const itemFieldId = item.match(/(\${([^}]*)})/)[2];
          if (!event[itemFieldId]) event[itemFieldId] = ["change"];
        });
      }
    });
  });

  const modalShowEvent = {};
  Object.keys(target).forEach((fieldId) => {
    const hasCompositeComponent = /(.*)>(.*)(:?)(.*)/.test(fieldId);
    if (hasCompositeComponent) {
      const [id, mainFieldId, subFieldId] = fieldId.match(/(.*)>(.*)(:?)(.*)/);
      modalShowEvent[mainFieldId] = ["onEditModalShow"];
    }
  });

  return {
    event,
    modalShowEvent,

    // 预处理，request 场景下默认开启
    preHandler: () => {
      Object.keys(target).forEach((fieldId) => {
        // 滤除未在页面显示的字段
        if (!fieldApi.isFieldExist(fieldId)) return;

        const { props: inProps } = target[fieldId];
        const componentName = fieldApi.getComponentName(fieldId);

        Object.keys(inProps || {}).forEach((propName) => {
          switch (propName) {
            // 设置选项，预先重置
            case "dataSource.options":
            case "options":
              if (componentName === "CascadeSelect") {
                fieldApi.setFieldProps(fieldId, {
                  hasError: false,
                  errorMsg: "",
                });
              } else {
                fieldApi.setFieldProps(fieldId, {
                  [propName]: [],
                  hasError: false,
                  errorMsg: "",
                });
              }

              break;
            case "value":
              fieldApi.setFieldProps(fieldId, {
                hasError: false,
                errorMsg: "",
              });
              break;
            default:
              break;
          }
        });
      });
    },

    // 响应数据通过 data 注入解析获得的 func 中
    handler: (data = {}) => {
      const interationParse = (template) => {
        // 递归迭代赋值
        Object.keys(template).forEach((key) => {
          const templateValue = template[key];
          if (isObject(templateValue)) {
            template[key] = interationParse(templateValue);
          } else if (isString(templateValue)) {
            template[key] = funcParser.parse(templateValue, options)(data);
          }
        });
        return template;
      };

      Object.keys(target).forEach((fieldId) => {
        // 滤除未在页面显示的字段
        if (!fieldApi.isFieldExist(fieldId)) return;
        const fieldValue = fieldApi.getFieldValue(fieldId);
        const componentName = fieldApi.getComponentName(fieldId);

        let props = {};
        const { props: inProps } = target[fieldId];
        const propNames = Object.keys(inProps || {});

        if (propNames.length) {
          propNames.forEach((propName) => {
            try {
              const template = JSON.parse(JSON.stringify(inProps[propName]));
              if (isString(template)) {
                props[propName] = funcParser.parse(template, options)(data);
              } else if (PROPS_NEED_ITERATION.includes(propName)) {
                // 迭代赋值
                props[propName] = interationParse(template);
              } else {
                props[propName] = template;
              }
              if (props[propName] === undefined) delete props[propName];
            } catch (err) {
              console.warn(err);
            }
          });

          if (isDetail) {
            // 详情页联动只会影响写死的几种属性 在这个地方过滤对象中的非 PROPS_IN_DETAIL 属性
            props = filterDetailProps({ componentName }, props);
          } else {
            // 通过联动关系设置 visible 属性为 false，默认提交时不收集数据
            if (
              propNames.includes("visible") &&
              !propNames.includes("disableCollect")
            ) {
              if (props.visible) props.disableCollect = false;
              else props.disableCollect = true;
            }

            // 如果有草稿，且草稿值不为空，则用草稿值覆盖，删除 props 中的 value 属性
            if (
              data.initial &&
              isEdit &&
              overwrite === true &&
              !isNil(fieldValue)
            ) {
              delete props.value;
            }

            props.dataShare = true;
            // 设置联动异步查询接口 注意，这里的 failed 仅仅代表接口返回数据为空，不代表这次Request请求失败
            props.failed = !!data.failed;
          }

          fieldApi.setFieldProps(fieldId, props, {
            convertFlag: true,
            changedKey: data.changedKey,
            fieldsShouldReset,
          });
        }
      });
    },
  };
};
